import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { Container } from "@/components/site/Container";
import { Overline } from "@/components/site/Overline";
import { LocaleLink } from "@/components/site/LocaleLink";
import { Button } from "@/components/ui/button";
import { OPEN_CONSENT_EVENT } from "@/components/site/cookie-consent";
import { business, addressLine } from "@/content/site";

export type LegalDoc = "privacy" | "cookies";

type LegalSection = {
  title: string;
  paragraphs?: string[];
  items?: string[];
  after?: string[];
  links?: { label: string; url: string }[];
  table?: { headers: string[]; rows: string[][] };
  consentButton?: boolean;
};

/** Date of the latest legal text revision. */
export const LEGAL_UPDATED = new Date("2026-10-18T12:00:00Z");

const linkClass =
  "rounded-[4px] text-brand-deep-water underline underline-offset-4 transition-colors duration-200 ease-out hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring";

export function LegalPage({ doc }: { doc: LegalDoc }) {
  const { t, i18n } = useTranslation("legal");
  const { t: tc } = useTranslation("common");
  const vars = {
    company: business.legalName,
    address: addressLine,
    vat: business.vat,
    email: business.email,
    phone: business.phone,
    site: "goproprojects.be",
  };
  const sections = t(`${doc}.sections`, { returnObjects: true, ...vars }) as LegalSection[];
  const locale = i18n.language === "fr" ? "fr-BE" : i18n.language === "en" ? "en-BE" : "nl-BE";
  const date = new Intl.DateTimeFormat(locale, { dateStyle: "long", timeZone: "Europe/Brussels" }).format(LEGAL_UPDATED);
  const other = doc === "privacy" ? "/cookiebeleid" : "/privacybeleid";

  return (
    <div className="bg-background pb-16 pt-28 md:pb-[120px] md:pt-40">
      <Container>
        <article className="max-w-[70ch]">
          <Overline>{t(`${doc}.overline`)}</Overline>
          <h1 className="mt-4 font-display text-[clamp(2rem,4vw,2.75rem)] font-bold leading-tight text-foreground">{t(`${doc}.heading`)}</h1>
          <p className="mt-4 text-sm text-muted-foreground">{t("updatedLabel", { date })}</p>
          <p className="mt-6 text-lg leading-8 text-foreground">{t(`${doc}.intro`, vars)}</p>

          {sections.map((s) => (
            <section key={s.title} className="mt-12">
              <h2 className="font-display text-2xl font-semibold text-foreground">{s.title}</h2>
              {s.paragraphs?.map((p) => <p key={p} className="mt-4 leading-7 text-foreground">{p}</p>)}
              {s.items && (
                <ul className="mt-4 list-disc space-y-2 pl-6 leading-7 text-foreground">
                  {s.items.map((i) => <li key={i}>{i}</li>)}
                </ul>
              )}
              {s.after?.map((p) => <p key={p} className="mt-4 leading-7 text-foreground">{p}</p>)}
              {s.table && (
                <div className="mt-6 overflow-x-auto rounded-lg border border-border">
                  <table className="w-full min-w-[560px] text-left text-sm">
                    <thead className="bg-surface">
                      <tr>{s.table.headers.map((h) => <th key={h} scope="col" className="px-4 py-3 font-semibold text-foreground">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {s.table.rows.map((row) => (
                        <tr key={row[0]} className="border-t border-border">
                          {row.map((cell, i) => i === 0
                            ? <th key={i} scope="row" className="px-4 py-3 font-mono text-xs font-medium text-foreground">{cell}</th>
                            : <td key={i} className="px-4 py-3 text-foreground">{cell}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {s.consentButton && (
                <Button className="mt-6" onClick={() => window.dispatchEvent(new Event(OPEN_CONSENT_EVENT))}>
                  {t("cookies.changeButton")}
                </Button>
              )}
              {s.links && (
                <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
                  {s.links.map((l) => (
                    <li key={l.url}><a href={l.url} target="_blank" rel="noopener noreferrer" className={linkClass}>{l.label}</a></li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <p className="mt-16 border-t border-border pt-8">
            <LocaleLink to={other} className={`inline-flex items-center gap-2 ${linkClass}`}>
              {tc(doc === "privacy" ? "footer.cookies" : "footer.privacy")}
              <ArrowRight aria-hidden="true" strokeWidth={1.75} className="h-4 w-4" />
            </LocaleLink>
          </p>
        </article>
      </Container>
    </div>
  );
}
